import { message } from "antd";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ENV } from "./constants/env";
import { AuthProvider } from "./contexts/auth-context";
import { ProtectedRoute } from "./services/protected-route";
import { Login } from "./views/Login/Login";
import { AcceptInvitation } from "./views/AcceptInvitation/AcceptInvitation";

export const App: FC = () => {
	message.config({ maxCount: 1 });
	useScript("https://static.matterport.com/showcase-sdk/latest.js");

	function useScript(src: string | null): string {
		const [status, setStatus] = useState<string>(src ? "loading" : "idle");

		useEffect(() => {
			if (!src) {
				setStatus("idle");
				return;
			}

			let script = document.querySelector<HTMLScriptElement>(
				`script[src="${src}"]`,
			);
			if (!script) {
				script = document.createElement("script");
				script.src = src;
				script.async = true;
				script.setAttribute("data-status", "loading");
				document.body.appendChild(script);

				const setAttributeFromEvent = (event: Event) => {
					script?.setAttribute(
						"data-status",
						event.type === "load" ? "ready" : "error",
					);
				};
				script.addEventListener("load", setAttributeFromEvent);
				script.addEventListener("error", setAttributeFromEvent);
			} else {
				setStatus(script.getAttribute("data-status") || "loading");
			}

			const setStateFromEvent = (event: Event) => {
				setStatus(event.type === "load" ? "ready" : "error");
			};

			script.addEventListener("load", setStateFromEvent);
			script.addEventListener("error", setStateFromEvent);

			return () => {
				script?.removeEventListener("load", setStateFromEvent);
				script?.removeEventListener("error", setStateFromEvent);
			};
		}, [src]);

		return status;
	}

	useEffect(() => {
		try {
			if (ENV === "production" && "serviceWorker" in navigator) {
				navigator.serviceWorker
					.register("/service-worker.js")
					.then((registration) => {
						console.log(
							"Service Worker registered with scope:",
							registration.scope,
						);

						// Check for updates immediately
						registration.update();

						// Check for updates when the page regains focus
						window.addEventListener("focus", () => {
							registration.update();
						});

						// Listen for service worker updates
						registration.addEventListener("updatefound", () => {
							const newWorker = registration.installing;
							if (newWorker) {
								newWorker.addEventListener("statechange", () => {
									if (
										newWorker.state === "installed" &&
										navigator.serviceWorker.controller
									) {
										// New service worker is ready - notify it to skip waiting
										newWorker.postMessage({ type: "SKIP_WAITING" });

										// Optionally notify the user
										console.log(
											"[ServiceWorker] New version available, activating...",
										);
									}
								});
							}
						});

						// Listen for the controller change (new SW took over)
						navigator.serviceWorker.addEventListener("controllerchange", () => {
							console.log(
								"[ServiceWorker] New version activated, reloading page...",
							);
							// Only reload if the page is visible to avoid disrupting the user
							if (!document.hidden) {
								window.location.reload();
							}
						});
					})
					.catch((error) => {
						console.log("Service Worker registration failed:", error);
					});
			}
		} catch (error) {
			console.log("Service Worker registration failed:", error);
		}
	}, []);

	return (
		<AuthProvider>
			<BrowserRouter>
				<Routes>
					<Route path="/login" element={<Login />} />
					<Route path="/accept-invitation/:token" element={<AcceptInvitation />} />
					<Route path="/*" element={<ProtectedRoute />} />
				</Routes>
			</BrowserRouter>
		</AuthProvider>
	);
};
